import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from './echartsUtils';
import {
  formatTrend,
  displayName,
  SENTIMENT_WORD_COLORS,
  SentimentLabel,
  TREND_COLORS,
  WordCloudItem,
} from './wordCloudUtils';
import styles from './ChartWordCloud.module.scss';

interface WordCloudListProps {
  items: WordCloudItem[];
  /** One colour per item, the same the cloud uses. */
  colors: string[];
  showTrend: boolean;
  showSentiment?: boolean;
  sentimentLabels?: Record<SentimentLabel, string>;
  trendLabel: string;
  onSelect?: (item: WordCloudItem) => void;
  onHide?: (item: WordCloudItem) => void;
}

export const TREND_GLYPHS = { rising: '▲', falling: '▼', steady: '●' } as const;
export const SENTIMENT_GLYPH = '●';

/**
 * The same data as the cloud, as a ranked table: exact values, and every action
 * (open, hide) reachable by keyboard and readable by a screen reader.
 */
export const WordCloudList: React.FC<WordCloudListProps> = ({
  items,
  colors,
  showTrend,
  showSentiment = false,
  sentimentLabels,
  trendLabel,
  onSelect,
  onHide,
}) => {
  const { t } = useTranslation();
  const max = items.length ? items[0].value : 1;

  return (
    <div className={styles.listWrap}>
      <table className={styles.list}>
        <caption className={styles.srOnly}>{t('Word frequency')}</caption>
        <thead>
          <tr>
            <th scope="col" className={styles.rankCol}>
              #
            </th>
            <th scope="col">{t('Word')}</th>
            <th scope="col">{t('Mentions')}</th>
            {showTrend && (
              <th scope="col" title={trendLabel}>
                {t('Trend')}
              </th>
            )}
            {showSentiment && <th scope="col">{t('Sentiment')}</th>}
            {onHide && (
              <th scope="col">
                <span className={styles.srOnly}>{t('Actions')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={`${item.kind}:${item.name}`}>
              <td className={styles.rankCol}>{i + 1}</td>
              <th scope="row" className={styles.wordCol}>
                {onSelect ? (
                  <button
                    type="button"
                    className={styles.wordButton}
                    onClick={() => onSelect(item)}
                  >
                    {displayName(item)}
                  </button>
                ) : (
                  displayName(item)
                )}
              </th>
              <td className={styles.valueCol}>
                <span className={styles.barTrack}>
                  <span
                    className={styles.bar}
                    style={{
                      width: `${Math.max(1, (item.value / max) * 100)}%`,
                      background: colors[i],
                    }}
                  />
                </span>
                <span className={styles.value}>{formatNumber(item.value)}</span>
              </td>
              {showTrend && (
                <td className={styles.trendCol}>
                  {item.trend ? (
                    <>
                      <span
                        aria-hidden="true"
                        style={{ color: TREND_COLORS[item.trend.direction] }}
                      >
                        {TREND_GLYPHS[item.trend.direction]}
                      </span>{' '}
                      {formatTrend(item.trend.change)}
                    </>
                  ) : (
                    <span className={styles.muted}>–</span>
                  )}
                </td>
              )}
              {showSentiment && (
                <td className={styles.trendCol}>
                  {item.sentiment ? (
                    <>
                      <span
                        aria-hidden="true"
                        style={{
                          color: SENTIMENT_WORD_COLORS[item.sentiment.label],
                        }}
                      >
                        {SENTIMENT_GLYPH}
                      </span>{' '}
                      {sentimentLabels?.[item.sentiment.label] ??
                        item.sentiment.label}
                    </>
                  ) : (
                    <span className={styles.muted}>–</span>
                  )}
                </td>
              )}
              {onHide && (
                <td className={styles.actionCol}>
                  <button
                    type="button"
                    className={styles.hideButton}
                    data-html2canvas-ignore="true"
                    aria-label={t('Hide {{word}}', { word: item.name })}
                    title={t('Hide {{word}}', { word: item.name })}
                    onClick={() => onHide(item)}
                  >
                    ×
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
